import { useEffect, useState, useCallback } from 'react';
import { Icon } from '../Icon/Icon';
import { useDisplayMediaPreviewSource } from '../../../../../services/displayApi';
import type { DisplayOrientation } from '../../../data/types';
import './MediaCarouselPreviewModal.css';

export interface PreviewMediaItem {
  id?: string;
  mediaId?: string;
  name: string;
  url?: string;
  mediaUrl?: string;
  mimeType?: string;
  orientation?: DisplayOrientation | null;
}

interface MediaCarouselPreviewModalProps {
  open: boolean;
  title?: string;
  items: PreviewMediaItem[];
  initialIndex?: number;
  onClose: () => void;
}

const isPortraitOrientation = (orientation?: string | null) => (orientation ?? '').toLowerCase() === 'portrait';

function MediaSlideView({ item, title }: { item: PreviewMediaItem; title?: string }) {
  const { mediaUrl, handleMediaError } = useDisplayMediaPreviewSource(item);
  const isVideo = item.mimeType?.startsWith('video/');
  const isPortrait = isPortraitOrientation(item.orientation);

  const previewBoxStyle: React.CSSProperties = isPortrait
    ? { width: 'min(38vw, 420px)', height: 'min(82vh, 760px)', borderRadius: 14 }
    : { width: 'min(82vw, 1200px)', height: 'min(62vw, 675px)', maxHeight: '82vh', borderRadius: 14 };

  return (
    <div className="media-carousel-slide-box">
      <div className="media-carousel-slide-inner">
        {mediaUrl ? (
          isVideo ? (
            <video
              src={mediaUrl}
              style={previewBoxStyle}
              className="media-carousel-slide-media"
              controls
              autoPlay
              muted
              onError={handleMediaError}
            />
          ) : (
            <img
              src={mediaUrl}
              alt={item.name || title || 'Media Preview'}
              style={previewBoxStyle}
              className="media-carousel-slide-media"
              onError={handleMediaError}
            />
          )
        ) : (
          <div
            style={previewBoxStyle}
            className="media-carousel-slide-fallback"
          >
            <Icon name={isVideo ? 'videocam' : 'image'} size={48} color="#64748B" />
            <div className="media-carousel-slide-fallback-title">{item.name || title || 'Media Asset'}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function MediaCarouselPreviewModal({
  open,
  title,
  items,
  initialIndex = 0,
  onClose,
}: MediaCarouselPreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex >= 0 && initialIndex < items.length ? initialIndex : 0);
    }
  }, [open, initialIndex, items.length]);

  const handlePrev = useCallback(() => {
    if (items.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  const handleNext = useCallback(() => {
    if (items.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose, handlePrev, handleNext]);

  if (!open || items.length === 0) return null;

  const currentItem = items[currentIndex] || items[0];
  const hasMultiple = items.length > 1;

  return (
    <div
      onClick={onClose}
      className="media-carousel-modal-backdrop"
    >
      <button
        onClick={onClose}
        aria-label="Close preview"
        className="media-carousel-modal-close-btn"
      >
        <Icon name="close" size={24} color="#fff" />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="media-carousel-slide-wrapper"
      >
        <MediaSlideView item={currentItem} title={title} />

        {hasMultiple && (
          <div className="media-carousel-nav-group">
            <div className="media-carousel-counter-badge">
              {currentIndex + 1} of {items.length} &bull; {currentItem.name || title || 'Media'}
            </div>

            <div className="media-carousel-dots-row">
              {items.map((_, idx) => {
                const isActive = idx === currentIndex;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentIndex(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`media-carousel-dot-btn ${isActive ? 'is-active' : ''}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {hasMultiple && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          aria-label="Previous slide"
          className="media-carousel-arrow-btn is-left"
        >
          <Icon name="chevron_left" size={32} color="#fff" />
        </button>
      )}

      {hasMultiple && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          aria-label="Next slide"
          className="media-carousel-arrow-btn is-right"
        >
          <Icon name="chevron_right" size={32} color="#fff" />
        </button>
      )}
    </div>
  );
}
